import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import {
  getAllDealers,
  getDealerWithOrders,
  updateDealerDiscount,
  updateDealerActive,
  sendPasswordSetupEmail,
  Dealer,
  DealerWithOrders,
} from '../services/dealers';
import './AdminDealersPage.css';

const AdminDealersPage: React.FC = () => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<DealerWithOrders | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDealer, setLoadingDealer] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [discountValue, setDiscountValue] = useState('');
  const [sendingPasswordEmail, setSendingPasswordEmail] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDealers();
  }, []);

  const loadDealers = async () => {
    setLoading(true);
    try {
      const data = await getAllDealers();
      setDealers(data);
    } catch (error) {
      console.error('Error loading dealers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDealerClick = async (dealerId: string) => {
    setLoadingDealer(true);
    try {
      const dealerData = await getDealerWithOrders(dealerId);
      setSelectedDealer(dealerData);
    } catch (error) {
      console.error('Error loading dealer details:', error);
      alert('Fehler beim Laden der Händlerdaten');
    } finally {
      setLoadingDealer(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedDealer(null);
    setEditingDiscount(false);
    setDiscountValue('');
  };

  const handleSaveDiscount = async () => {
    if (!selectedDealer) return;

    const discount = parseFloat(discountValue);
    if (isNaN(discount) || discount < 0 || discount > 100) {
      alert('Rabatt muss eine Zahl zwischen 0 und 100 sein');
      return;
    }

    try {
      const updatedDealer = await updateDealerDiscount(selectedDealer.id, discount);
      setSelectedDealer({ ...selectedDealer, discountPercent: updatedDealer.discountPercent });
      setEditingDiscount(false);
      setDiscountValue('');
      // Aktualisiere auch die Liste
      const updatedDealers = dealers.map(d =>
        d.id === updatedDealer.id ? { ...d, discountPercent: updatedDealer.discountPercent } : d
      );
      setDealers(updatedDealers);
    } catch (error) {
      console.error('Error updating discount:', error);
      alert('Fehler beim Aktualisieren des Rabatts');
    }
  };

  const handleSendPasswordSetupEmail = async () => {
    if (!selectedDealer) return;

    if (
      !window.confirm(
        `Möchtest du wirklich eine Passwort-Set-Mail an ${selectedDealer.email} senden?`
      )
    ) {
      return;
    }

    setSendingPasswordEmail(true);
    try {
      const result = await sendPasswordSetupEmail(selectedDealer.id);
      alert(result.message || 'Passwort-Set-Mail wurde erfolgreich gesendet.');
    } catch (error: any) {
      console.error('Error sending password setup email:', error);
      alert(error.response?.data?.error || 'Fehler beim Senden der Passwort-Set-Mail');
    } finally {
      setSendingPasswordEmail(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedDealer) return;

    const newActiveStatus = !selectedDealer.active;
    const action = newActiveStatus ? 'aktivieren' : 'deaktivieren';

    if (!window.confirm(`Möchtest du diesen Händler wirklich ${action}?`)) {
      return;
    }

    try {
      const updatedDealer = await updateDealerActive(selectedDealer.id, newActiveStatus);
      setSelectedDealer({ ...selectedDealer, active: updatedDealer.active });
      // Aktualisiere auch die Liste
      const updatedDealers = dealers.map(d =>
        d.id === updatedDealer.id ? { ...d, active: updatedDealer.active } : d
      );
      setDealers(updatedDealers);
      alert(`Händler wurde erfolgreich ${newActiveStatus ? 'aktiviert' : 'deaktiviert'}.`);
    } catch (error: any) {
      console.error('Error updating dealer active status:', error);
      alert(error.response?.data?.error || 'Fehler beim Aktualisieren des Status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  return (
    <div>
      <AdminHeader />
      <div className="container">
        <div className="dealers-layout">
          <div className="dealers-list-section">
            <h2>Alle Händler</h2>
            {loading ? (
              <div className="loading">Lade Händler...</div>
            ) : dealers.length === 0 ? (
              <div>Keine Händler gefunden.</div>
            ) : (
              <div className="dealers-list">
                {dealers.map(dealer => (
                  <div
                    key={dealer.id}
                    className={`dealer-card ${selectedDealer?.id === dealer.id ? 'selected' : ''}`}
                    onClick={() => handleDealerClick(dealer.id)}
                  >
                    <div className="dealer-card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{dealer.username}</strong>
                        {dealer.active === false && (
                          <span
                            style={{
                              fontSize: '0.75em',
                              padding: '2px 6px',
                              backgroundColor: '#ffc107',
                              color: '#000',
                              borderRadius: '4px',
                              fontWeight: 'bold',
                            }}
                          >
                            Inaktiv
                          </span>
                        )}
                      </div>
                      {dealer._count && (
                        <span className="order-count">
                          {dealer._count.orders} Bestellung{dealer._count.orders !== 1 ? 'en' : ''}
                        </span>
                      )}
                    </div>
                    <div className="dealer-card-body">
                      <div className="dealer-info">
                        <span className="dealer-email">{dealer.email}</span>
                        <span className="dealer-date">
                          Registriert: {formatDate(dealer.createdAt)}
                        </span>
                      </div>
                      {dealer.revenue && (
                        <div
                          className="dealer-revenue"
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '0.9em',
                            }}
                          >
                            <span>Letzter Monat:</span>
                            <strong>{dealer.revenue.lastMonth.toFixed(2)} €</strong>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: '0.9em',
                              marginTop: '0.25rem',
                            }}
                          >
                            <span>Aktueller Monat:</span>
                            <strong style={{ color: '#27ae60' }}>
                              {dealer.revenue.currentMonth.toFixed(2)} €
                            </strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="dealer-details-section">
            {loadingDealer ? (
              <div className="loading">Lade Händlerdetails...</div>
            ) : selectedDealer ? (
              <div className="dealer-details">
                <div className="dealer-details-header">
                  <h2>{selectedDealer.username}</h2>
                  <button onClick={handleCloseDetails} className="btn btn-secondary">
                    Schließen
                  </button>
                </div>
                <div className="dealer-info-card">
                  <h3>Kontaktinformationen</h3>
                  <p>
                    <strong>E-Mail:</strong> {selectedDealer.email}
                  </p>
                  <p>
                    <strong>Registriert am:</strong> {formatDate(selectedDealer.createdAt)}
                  </p>
                  <p>
                    <strong>Gesamtbestellungen:</strong> {selectedDealer.orders.length}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: selectedDealer.active ? '#d4edda' : '#fff3cd',
                        color: selectedDealer.active ? '#155724' : '#856404',
                        fontWeight: 'bold',
                      }}
                    >
                      {selectedDealer.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </p>
                  <div
                    style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: selectedDealer.active ? '#d4edda' : '#fff3cd',
                      borderRadius: '4px',
                      border: `1px solid ${selectedDealer.active ? '#28a745' : '#ffc107'}`,
                    }}
                  >
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Händlerzugang</h4>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9em', color: '#666' }}>
                      {selectedDealer.active
                        ? 'Der Händler kann Bestellungen aufgeben.'
                        : 'Der Händlerzugang ist deaktiviert. Der Händler kann sich nicht anmelden und keine Bestellungen aufgeben.'}
                    </p>
                    <button
                      onClick={handleToggleActive}
                      className={`btn ${selectedDealer.active ? 'btn-warning' : 'btn-success'}`}
                    >
                      {selectedDealer.active ? 'Händler deaktivieren' : 'Händler aktivieren'}
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                    }}
                  >
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Händlerrabatt</h4>
                    {editingDiscount ? (
                      <div>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={discountValue}
                          onChange={e => setDiscountValue(e.target.value)}
                          placeholder="0"
                          style={{ width: '100px', padding: '0.5rem', marginRight: '0.5rem' }}
                        />
                        <span>%</span>
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            onClick={handleSaveDiscount}
                            className="btn btn-primary"
                            style={{ marginRight: '0.5rem' }}
                          >
                            Speichern
                          </button>
                          <button
                            onClick={() => {
                              setEditingDiscount(false);
                              setDiscountValue('');
                            }}
                            className="btn btn-secondary"
                          >
                            Abbrechen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {selectedDealer.discountPercent && selectedDealer.discountPercent > 0 ? (
                          <p style={{ margin: '0 0 0.5rem 0' }}>
                            <strong>Aktueller Rabatt:</strong> {selectedDealer.discountPercent}%
                          </p>
                        ) : (
                          <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}>
                            Kein Rabatt festgelegt
                          </p>
                        )}
                        <button
                          onClick={() => {
                            setEditingDiscount(true);
                            setDiscountValue(String(selectedDealer.discountPercent || 0));
                          }}
                          className="btn btn-primary"
                        >
                          Rabatt bearbeiten
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: '#e8f4f8',
                      borderRadius: '4px',
                      border: '1px solid var(--accent)',
                    }}
                  >
                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Passwort-Set-Mail</h4>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9em', color: '#666' }}>
                      Sende dem Händler eine E-Mail mit einem Link zum Setzen seines initialen
                      Passworts.
                    </p>
                    <button
                      onClick={handleSendPasswordSetupEmail}
                      className="btn btn-primary"
                      disabled={sendingPasswordEmail}
                    >
                      {sendingPasswordEmail ? 'Wird gesendet...' : 'Passwort-Set-Mail senden'}
                    </button>
                  </div>
                </div>
                <div className="dealer-orders-card">
                  <h3>Bestellungen ({selectedDealer.orders.length})</h3>
                  {selectedDealer.orders.length === 0 ? (
                    <p>Dieser Händler hat noch keine Bestellungen aufgegeben.</p>
                  ) : (
                    <div className="orders-table-wrapper">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Bestellnummer</th>
                            <th>Modell</th>
                            <th>Lieferung</th>
                            <th>Preis</th>
                            <th>Status</th>
                            <th>Datum</th>
                            <th>Aktionen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDealer.orders.map(order => (
                            <tr key={order.id}>
                              <td>
                                <strong>{order.orderNumber}</strong>
                              </td>
                              <td>{order.goggleModel?.name || 'Unbekannt'}</td>
                              <td>{order.deliveryType === 'SHOP' ? 'Shop' : 'Kunde'}</td>
                              <td>
                                {order.finalPrice !== undefined ? (
                                  <strong>{order.finalPrice.toFixed(2)} €</strong>
                                ) : (
                                  <span style={{ color: '#999' }}>N/A</span>
                                )}
                              </td>
                              <td>
                                <span
                                  className={`status-badge ${
                                    order.status === 'CANCELLED'
                                      ? 'cancelled'
                                      : order.status === 'COMPLETED'
                                        ? 'completed'
                                        : order.status === 'SHIPPED'
                                          ? 'shipped'
                                          : order.status === 'PARTS_ORDERED'
                                            ? 'parts-ordered'
                                            : 'pending'
                                  }`}
                                >
                                  {order.status === 'CANCELLED'
                                    ? 'Storniert'
                                    : order.status === 'COMPLETED'
                                      ? 'Erledigt'
                                      : order.status === 'SHIPPED'
                                        ? 'Versendet'
                                        : order.status === 'PARTS_ORDERED'
                                          ? 'Teile bestellt'
                                          : 'Ausstehend'}
                                </span>
                              </td>
                              <td>{formatDate(order.createdAt)}</td>
                              <td>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => navigate(`/admin/orders`)}
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="dealer-details-placeholder">
                <p>Wähle einen Händler aus der Liste aus, um dessen Bestellungen zu sehen.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDealersPage;
